import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import {
    Box,
    Stack,
    Typography,
    Select,
    MenuItem,
    Tooltip,
    IconButton,
    CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LanguageIcon from "@mui/icons-material/Language";
import MonitorIcon from "@mui/icons-material/Monitor";
import { getActivitySummary, saveDomainCategory } from "../../services/timeTracker.service";
import { encryptData } from "../../utils/cryptoUtils";

// Productivity bucket display metadata (label + colors) for badges and the card.
const PRODUCTIVITY_META = {
    productive: { label: "Productive", color: "#16a34a", bg: "#dcfce7" },
    neutral: { label: "Neutral", color: "#6b7280", bg: "#f3f4f6" },
    unproductive: { label: "Unproductive", color: "#dc2626", bg: "#fee2e2" },
};
const fmtMins = (m) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);

// Standalone Productivity page. Opened from the user's TimeTracker detail view via a
// "Productivity" button (carries userId / name / date in the query string). Shows the
// overall productive % + a per-website breakdown with editable productivity categories.
export default function ProductivityPage() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const userId = params.get("userId") || "";
    const userName = params.get("name") || "";
    const date = params.get("date") || dayjs().format("YYYY-MM-DD");

    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [savingKey, setSavingKey] = useState(null);

    const load = async () => {
        if (!userId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const res = await getActivitySummary(
                encryptData(`${date}T00:00:00Z`),
                encryptData(`${date}T23:59:59Z`),
                encryptData(userId)
            );
            setSummary(res?.data || null);
        } catch (err) {
            console.error("Error loading productivity:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, date]);

    // Change a domain/app productivity bucket. Encrypt ALL params (base64) like the other
    // timeLogger endpoints — plain text risks the backend mis-decoding values that happen
    // to be valid base64 (e.g. "productive").
    const handleChange = async (type, name, productivity) => {
        try {
            setSavingKey(`${type}:${name}`);
            await saveDomainCategory(
                encryptData(userId),
                encryptData(type),
                encryptData(name),
                encryptData(productivity)
            );
            await load();
        } catch (err) {
            console.error("save category failed", err);
        } finally {
            setSavingKey(null);
        }
    };

    // Small reusable category dropdown (color-coded), used by both apps and websites.
    const CategorySelect = ({ type, name, value }) => (
        <Select
            size="small"
            value={value || "neutral"}
            disabled={savingKey === `${type}:${name}`}
            onChange={(e) => handleChange(type, name, e.target.value)}
            sx={{
                height: 26,
                fontSize: 12,
                fontWeight: 600,
                color: PRODUCTIVITY_META[value || "neutral"].color,
                bgcolor: PRODUCTIVITY_META[value || "neutral"].bg,
                ".MuiOutlinedInput-notchedOutline": { border: "none" },
                ".MuiSelect-select": { py: 0.25, px: 1.25 },
            }}
        >
            {Object.entries(PRODUCTIVITY_META).map(([k, m]) => (
                <MenuItem key={k} value={k} sx={{ fontSize: 13, color: m.color, fontWeight: 600 }}>
                    {m.label}
                </MenuItem>
            ))}
        </Select>
    );

    const prod = summary?.productivity || null;
    const sites = summary?.topSites || [];
    const apps = (summary?.topApps || []).filter((a) => a.app && a.app !== "unknown");
    const maxSiteMin = Math.max(...sites.map((s) => s.minutes), 1);
    const maxAppMin = Math.max(...apps.map((a) => a.minutes), 1);

    return (
        <Box sx={{ p: 3, maxWidth: 760, mx: "auto" }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <IconButton onClick={() => navigate(-1)} size="small">
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    Insights
                </Typography>
                <Typography color="text.secondary" sx={{ ml: 1 }}>
                    {userName ? `${userName} · ` : ""}
                    {dayjs(date).format("ddd, MMM D YYYY")}
                </Typography>
            </Stack>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : apps.length === 0 && sites.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 4 }}>
                    No activity recorded for this date.
                </Typography>
            ) : (
                <>
                    {/* Overall productivity card */}
                    <Box sx={{ mb: 3, p: 2, borderRadius: 2, border: "1px solid #e5e7eb", bgcolor: "#fafafa" }}>
                        <Stack direction="row" alignItems="center" spacing={3}>
                            <Box sx={{ textAlign: "center", minWidth: 90 }}>
                                <Typography sx={{ fontSize: 40, fontWeight: 700, color: "#16a34a", lineHeight: 1 }}>
                                    {prod.productivityPct}%
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    productive
                                </Typography>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: "flex", height: 14, borderRadius: 7, overflow: "hidden", mb: 1, bgcolor: "#eee" }}>
                                    <Box sx={{ width: `${(prod.productiveMin / prod.totalMin) * 100}%`, bgcolor: "#16a34a" }} />
                                    <Box sx={{ width: `${(prod.neutralMin / prod.totalMin) * 100}%`, bgcolor: "#9ca3af" }} />
                                    <Box sx={{ width: `${(prod.unproductiveMin / prod.totalMin) * 100}%`, bgcolor: "#dc2626" }} />
                                </Box>
                                <Stack direction="row" spacing={2.5} flexWrap="wrap">
                                    {[
                                        ["productive", prod.productiveMin],
                                        ["neutral", prod.neutralMin],
                                        ["unproductive", prod.unproductiveMin],
                                    ].map(([k, m]) => (
                                        <Stack key={k} direction="row" alignItems="center" spacing={0.5}>
                                            <Box sx={{ width: 11, height: 11, borderRadius: "50%", bgcolor: PRODUCTIVITY_META[k].color }} />
                                            <Typography variant="body2" color="text.secondary">
                                                {PRODUCTIVITY_META[k].label} {fmtMins(m)}
                                            </Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            </Box>
                        </Stack>
                    </Box>

                    {/* Per-app breakdown with editable category (VS Code, MongoDB Compass, …) */}
                    {apps.length > 0 && (
                        <>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                                <MonitorIcon fontSize="small" /> Apps
                            </Typography>
                            <Stack spacing={1} sx={{ mb: 3 }}>
                                {apps.map((a) => (
                                    <Box key={a.app} sx={{ p: 1.25, borderRadius: 1.5, border: "1px solid #e5e7eb" }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.75}>
                                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                                <strong style={{ fontSize: 14 }}>{a.app}</strong>
                                                <CategorySelect type="app" name={a.app} value={a.productivity} />
                                            </Stack>
                                            <strong style={{ color: "#0369a1", fontSize: 13 }}>{fmtMins(a.minutes)}</strong>
                                        </Stack>
                                        <Box sx={{ height: 6, bgcolor: "#e0f2fe", borderRadius: 1 }}>
                                            <Box sx={{ width: `${(a.minutes / maxAppMin) * 100}%`, height: "100%", bgcolor: "#6366f1", borderRadius: 1 }} />
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        </>
                    )}

                    {/* Per-website breakdown with editable category */}
                    {sites.length > 0 && (
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
                        <LanguageIcon fontSize="small" /> Websites
                    </Typography>
                    )}
                    <Stack spacing={1}>
                        {sites.map((s) => (
                            <Box key={s.domain} sx={{ p: 1.25, borderRadius: 1.5, border: "1px solid #e5e7eb" }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.75}>
                                    <Stack direction="row" alignItems="center" spacing={1.5}>
                                        <Tooltip title={s.url || s.domain} placement="top" arrow>
                                            <strong style={{ fontSize: 14, cursor: "help" }}>{s.domain}</strong>
                                        </Tooltip>
                                        <CategorySelect type="domain" name={s.domain} value={s.productivity} />
                                    </Stack>
                                    <strong style={{ color: "#0369a1", fontSize: 13 }}>{fmtMins(s.minutes)}</strong>
                                </Stack>
                                <Box sx={{ height: 6, bgcolor: "#e0f2fe", borderRadius: 1 }}>
                                    <Box sx={{ width: `${(s.minutes / maxSiteMin) * 100}%`, height: "100%", bgcolor: "#0ea5e9", borderRadius: 1 }} />
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                </>
            )}
        </Box>
    );
}
